// ABOUTME: Displays child profile information with avatar, name, and family details
// ABOUTME: Two-column layout with profile card and family information grid

import React from 'react';
import {
  Box,
  Paper,
  Avatar,
  Typography,
  Chip
} from '@mui/material';
import { Person, Group } from '@mui/icons-material';
import { getAvatarUrl } from '../../utils/avatarUtils';
import { formatRelativeTime, formatFriendlyDate } from '../../utils/timeUtils';
import type { ChildDetail } from './types';

interface ChildInfoSectionProps {
  child: ChildDetail;
}

const ChildInfoSection: React.FC<ChildInfoSectionProps> = ({ child }) => {
  return (
    <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
      <Box sx={{ flex: '0 0 300px' }}>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Avatar
            src={getAvatarUrl(child.profileImageUrl)}
            sx={{ width: 120, height: 120, mx: 'auto', mb: 2 }}
          >
            <Person sx={{ fontSize: 60 }} />
          </Avatar>
          <Typography variant="h4" gutterBottom>
            {child.name}
          </Typography>
          {child.aliases && child.aliases.length > 0 && (
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Also known as: {child.aliases.join(', ')}
            </Typography>
          )}
          <Chip
            label={`Age ${child.age}`}
            color="primary"
            sx={{ mt: 1 }}
          />
        </Paper>
      </Box>

      <Box sx={{ flex: 1 }}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Group />
            Family Information
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 2, mt: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Family Name
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {child.family.name}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Behavior Records
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {child.behaviorRecordCount}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Last Recorded Behavior
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {child.lastBehaviorRecord ? formatRelativeTime(child.lastBehaviorRecord, child.family?.timezone) : 'No activity'}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Member Since
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {formatFriendlyDate(child.createdAt)}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default ChildInfoSection;
